import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useOptimizedPerformanceMonitoring } from "@/hooks/useOptimizedPerformanceMonitoring";
import { useAdvancedTheme } from "@/hooks/useAdvancedTheme";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import AdvancedErrorBoundary from "@/components/common/AdvancedErrorBoundary";
import PWAInstallPrompt from "@/components/common/PWAInstallPrompt";
import { AppRouter } from "@/router/AppRouter";
import { EditModeProvider } from "@/context/EditModeContext";
import { SmoothScroll } from "@/components/ui/smooth-scroll";
import { AccessibilityImprover } from "@/components/common/AccessibilityImprover";
import "./styles/background-fixes.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 15 * 60 * 1000, // 15 minutes
      retry: 1, // Reduced retries
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

const AppContent = () => {
  useOptimizedPerformanceMonitoring();
  const { actualTheme } = useAdvancedTheme();
  useKeyboardNavigation({
    onEscape: () => {
      const closeButtons = document.querySelectorAll('[data-dismiss="modal"], [data-dismiss="dialog"]');
      closeButtons.forEach(button => (button as HTMLElement).click());
    }
  });

  return (
    <SmoothScroll>
      <div className={`min-h-screen transition-colors duration-300 ${actualTheme === 'dark' ? 'dark' : ''}`}>
        <AccessibilityImprover />
        <AppRouter />
        <PWAInstallPrompt />
      </div>
    </SmoothScroll>
  );
};

const App = () => (
  <AdvancedErrorBoundary context="root">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <EditModeProvider>
          <Toaster />
          <Sonner />
          <AppContent />
        </EditModeProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </AdvancedErrorBoundary>
);

export default App;
